// SupplierForm.js — formulário compartilhado de cadastro/edição de fornecedor.
// Bloco 7 Fase B: PF/PJ (CPF ou CNPJ) + endereço estruturado (logradouro/cidade/UF/CEP).
import React, { useState } from 'react';
import { toast } from 'sonner';
import { X, Save, Search, Loader2 } from 'lucide-react';
import { authHeaders } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const onlyDigits = (s) => (s || '').replace(/\D/g, '');

const formatCpfDisplay = (s) => {
  const d = onlyDigits(s);
  if (d.length !== 11) return s || '';
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9, 11)}`;
};
const formatCnpjDisplay = (s) => {
  const d = onlyDigits(s);
  if (d.length !== 14) return s || '';
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12, 14)}`;
};
const formatDocumentoDisplay = (s, tipo) => {
  const d = onlyDigits(s);
  if (tipo === 'PF') return d.length === 11 ? formatCpfDisplay(s) : s || '';
  return d.length === 14 ? formatCnpjDisplay(s) : s || '';
};
const maskCnpj = (s) => {
  const d = onlyDigits(s).slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};
const maskCpf = (s) => {
  const d = onlyDigits(s).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};
const maskCep = (s) => {
  const d = onlyDigits(s).slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
};

const UFS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS',
  'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC',
  'SP', 'SE', 'TO',
];

const Field = ({ label, children, full = false }) => (
  <div className={full ? 'col-span-2' : ''}>
    <label className="block text-xs text-gray-400 mb-1">{label}</label>
    {children}
  </div>
);

export default function SupplierForm({ fornecedor, onClose, onSaved, compact = false, defaultNome = '' }) {
  const initialTipo = fornecedor?.tipo_pessoa || 'PJ';
  const initialDocRaw = fornecedor?.documento || fornecedor?.cnpj || '';
  const [form, setForm] = useState({
    nome: fornecedor?.nome ?? defaultNome,
    tipo: fornecedor?.tipo || 'Externo',
    ativo: fornecedor?.ativo ?? true,
    tipo_pessoa: initialTipo,
    documento: initialDocRaw ? formatDocumentoDisplay(initialDocRaw, initialTipo) : '',
    logradouro: fornecedor?.logradouro || '',
    numero: fornecedor?.numero || '',
    complemento: fornecedor?.complemento || '',
    bairro: fornecedor?.bairro || '',
    cidade: fornecedor?.cidade || '',
    uf: fornecedor?.uf || '',
    cep: fornecedor?.cep ? maskCep(fornecedor.cep) : '',
    telefone: fornecedor?.telefone || '',
    pessoa_contato: fornecedor?.pessoa_contato || '',
    email: fornecedor?.email || '',
    observacoes: fornecedor?.observacoes || '',
  });
  const [saving, setSaving] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);

  const switchTipoPessoa = (novo) => {
    setForm((prev) => ({ ...prev, tipo_pessoa: novo, documento: '' }));
  };
  const handleDocChange = (raw) => {
    setForm((prev) => ({
      ...prev,
      documento: prev.tipo_pessoa === 'PF' ? maskCpf(raw) : maskCnpj(raw),
    }));
  };

  // Consulta automática na Receita Federal ao sair do campo CNPJ
  const handleCnpjBlur = async () => {
    if (form.tipo_pessoa !== 'PJ') return;
    const digits = onlyDigits(form.documento);
    if (digits.length !== 14) return;
    setLookingUp(true);
    try {
      const r = await fetch(`${API_URL}/api/fornecedores/consulta-cnpj/${digits}`, {
        headers: authHeaders(),
      });
      if (!r.ok) {
        toast.error('Consulta indisponível — preencha manualmente');
        return;
      }
      const d = await r.json();
      if (!d.available) {
        toast.error(d.error || 'CNPJ não encontrado — preencha manualmente');
        return;
      }
      // Preenche apenas campos vazios (não sobrescreve o que o usuário já digitou)
      setForm((prev) => ({
        ...prev,
        nome: prev.nome?.trim() ? prev.nome : (d.razao_social || prev.nome),
        logradouro: prev.logradouro || d.logradouro || '',
        numero: prev.numero || d.numero || '',
        complemento: prev.complemento || d.complemento || '',
        bairro: prev.bairro || d.bairro || '',
        cidade: prev.cidade || d.cidade || '',
        uf: prev.uf || d.uf || '',
        cep: prev.cep || (d.cep ? maskCep(d.cep) : ''),
        telefone: prev.telefone || d.telefone || '',
        email: prev.email || d.email || '',
      }));
      const situacao = d.situacao_cadastral ? ` • ${d.situacao_cadastral}` : '';
      toast.success(`Dados preenchidos da Receita Federal${situacao}`);
    } catch (e) {
      toast.error('Consulta indisponível — preencha manualmente');
    } finally {
      setLookingUp(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.nome.trim()) return toast.error('Nome obrigatório');
    setSaving(true);
    try {
      const docDigits = onlyDigits(form.documento);
      const expectedLen = form.tipo_pessoa === 'PF' ? 11 : 14;
      if (docDigits && docDigits.length !== expectedLen) {
        setSaving(false);
        return toast.error(`${form.tipo_pessoa === 'PF' ? 'CPF' : 'CNPJ'} deve conter ${expectedLen} dígitos`);
      }
      const body = {
        nome: form.nome.trim(),
        tipo: form.tipo,
        ativo: form.ativo,
        tipo_pessoa: form.tipo_pessoa,
        documento: docDigits || null,
        logradouro: form.logradouro.trim() || null,
        numero: form.numero.trim() || null,
        complemento: form.complemento.trim() || null,
        bairro: form.bairro.trim() || null,
        cidade: form.cidade.trim() || null,
        uf: form.uf || null,
        cep: onlyDigits(form.cep) || null,
        telefone: form.telefone.trim() || null,
        pessoa_contato: form.pessoa_contato.trim() || null,
        email: form.email.trim() || null,
        observacoes: form.observacoes.trim() || null,
      };
      const url = fornecedor ? `${API_URL}/api/fornecedores/${fornecedor.id}` : `${API_URL}/api/fornecedores`;
      const r = await fetch(url, {
        method: fornecedor ? 'PUT' : 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const d = await r.json();
        throw new Error(d.detail || 'Erro ao salvar');
      }
      const saved = await r.json();
      toast.success(fornecedor ? 'Fornecedor atualizado' : 'Fornecedor cadastrado');
      onSaved?.(saved);
    } catch (e2) {
      toast.error(e2.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="supplier-form">
      {!compact && (
        <div className="flex items-center justify-between pb-2 border-b border-gray-800">
          <h3 className="text-white font-semibold">{fornecedor ? 'Editar Fornecedor' : 'Novo Fornecedor'}</h3>
          {onClose && (
            <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-white"><X size={18} /></button>
          )}
        </div>
      )}

      {/* PF / PJ toggle */}
      <div className="flex gap-2" data-testid="supplier-form-tipo-pessoa-toggle">
        <button type="button" onClick={() => switchTipoPessoa('PJ')}
                data-testid="supplier-form-tp-pj"
                className={`flex-1 px-3 py-2 rounded-lg border text-sm transition-colors ${form.tipo_pessoa === 'PJ' ? 'bg-yellow-500/20 border-yellow-500 text-yellow-300' : 'bg-[#0A0A0A] border-gray-800 text-gray-400'}`}>
          Pessoa Jurídica (CNPJ)
        </button>
        <button type="button" onClick={() => switchTipoPessoa('PF')}
                data-testid="supplier-form-tp-pf"
                className={`flex-1 px-3 py-2 rounded-lg border text-sm transition-colors ${form.tipo_pessoa === 'PF' ? 'bg-yellow-500/20 border-yellow-500 text-yellow-300' : 'bg-[#0A0A0A] border-gray-800 text-gray-400'}`}>
          Pessoa Física (CPF)
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label={form.tipo_pessoa === 'PF' ? 'Nome Completo *' : 'Razão Social *'} full>
          <input data-testid="supplier-form-nome" value={form.nome}
                 onChange={(e) => setForm({ ...form, nome: e.target.value })}
                 className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" />
        </Field>
        <Field label={form.tipo_pessoa === 'PF' ? 'CPF' : 'CNPJ'}>
          <div className="relative">
            <input data-testid="supplier-form-documento" value={form.documento}
                   placeholder={form.tipo_pessoa === 'PF' ? '000.000.000-00' : '00.000.000/0000-00'}
                   onChange={(e) => handleDocChange(e.target.value)}
                   onBlur={handleCnpjBlur}
                   className="w-full px-3 py-2 pr-9 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" />
            {form.tipo_pessoa === 'PJ' && (
              <span
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
                title="Consultaremos a Receita Federal ao sair do campo"
              >
                {lookingUp ? <Loader2 size={14} className="animate-spin text-yellow-400" /> : <Search size={14} />}
              </span>
            )}
          </div>
        </Field>
        <Field label="Tipo">
          <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm">
            <option value="Externo">Fornecedor Externo</option>
            <option value="Local">Local de Operação</option>
            <option value="Nao Classificado">Não Classificado</option>
          </select>
        </Field>
        <Field label="Telefone">
          <input data-testid="supplier-form-telefone" value={form.telefone} placeholder="(00) 00000-0000"
                 onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                 className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" />
        </Field>
        <Field label="Pessoa de Contato">
          <input data-testid="supplier-form-contato" value={form.pessoa_contato}
                 onChange={(e) => setForm({ ...form, pessoa_contato: e.target.value })}
                 className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" />
        </Field>
        <Field label="E-mail" full>
          <input data-testid="supplier-form-email" type="email" value={form.email}
                 onChange={(e) => setForm({ ...form, email: e.target.value })}
                 className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" />
        </Field>
      </div>

      {/* Endereço estruturado */}
      <div className="border border-gray-800 rounded-lg p-3 bg-[#0A0A0A]/50">
        <p className="text-xs text-gray-400 uppercase mb-2">Endereço</p>
        <div className="grid grid-cols-6 gap-2">
          <div className="col-span-4">
            <label className="block text-xs text-gray-400 mb-1">Logradouro</label>
            <input data-testid="supplier-form-logradouro" value={form.logradouro}
                   onChange={(e) => setForm({ ...form, logradouro: e.target.value })}
                   className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white text-sm" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs text-gray-400 mb-1">Nº / Lote</label>
            <input data-testid="supplier-form-numero" value={form.numero}
                   onChange={(e) => setForm({ ...form, numero: e.target.value })}
                   className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white text-sm" />
          </div>
          <div className="col-span-3">
            <label className="block text-xs text-gray-400 mb-1">Complemento</label>
            <input value={form.complemento}
                   onChange={(e) => setForm({ ...form, complemento: e.target.value })}
                   className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white text-sm" />
          </div>
          <div className="col-span-3">
            <label className="block text-xs text-gray-400 mb-1">Bairro</label>
            <input value={form.bairro}
                   onChange={(e) => setForm({ ...form, bairro: e.target.value })}
                   className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white text-sm" />
          </div>
          <div className="col-span-3">
            <label className="block text-xs text-gray-400 mb-1">Cidade</label>
            <input data-testid="supplier-form-cidade" value={form.cidade}
                   onChange={(e) => setForm({ ...form, cidade: e.target.value })}
                   className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">UF</label>
            <select data-testid="supplier-form-uf" value={form.uf}
                    onChange={(e) => setForm({ ...form, uf: e.target.value })}
                    className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white text-sm">
              <option value="">—</option>
              {UFS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-xs text-gray-400 mb-1">CEP</label>
            <input data-testid="supplier-form-cep" value={form.cep} placeholder="00000-000"
                   onChange={(e) => setForm({ ...form, cep: maskCep(e.target.value) })}
                   className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded text-white text-sm" />
          </div>
        </div>
      </div>

      {!compact && (
        <Field label="Observações" full>
          <textarea rows={2} value={form.observacoes}
                    onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                    className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm" />
        </Field>
      )}

      <div className="flex justify-end gap-2 pt-1">
        {onClose && (
          <button type="button" onClick={onClose}
                  className="px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white text-sm rounded-lg">
            Cancelar
          </button>
        )}
        <button type="submit" disabled={saving} data-testid="supplier-form-save"
                className="flex items-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold text-sm rounded-lg disabled:opacity-50">
          <Save size={14} /> {saving ? 'Salvando...' : (fornecedor ? 'Atualizar' : 'Cadastrar')}
        </button>
      </div>
    </form>
  );
}

export { formatCnpjDisplay, formatCpfDisplay, formatDocumentoDisplay };
